import React from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { AgGridReact } from 'ag-grid-react';
import { Column, IRowNode } from 'ag-grid-community';
import { ContentTable, PageOrientation, PredefinedPageSize } from 'pdfmake/interfaces';
// pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = {
  SpoqahanSansNeo: {
    normal: 'https://wesop.s3.ap-northeast-2.amazonaws.com/fonts/SpoqaHanSansNeo-Regular.ttf',
    bold: 'https://wesop.s3.ap-northeast-2.amazonaws.com/fonts/SpoqaHanSansNeo-Bold.ttf',
  },
};

const useGridExport = () => {
  /**
   * This function iterates over all of the columns to create a row of header cells
   */
  const getHeaderToExport = (gridApi: AgGridReact) => {
    const columns = gridApi.api.getAllDisplayedColumns();

    return columns.map((column) => {
      const { field } = column.getColDef();
      const sort = column.getSort();
      // Enables export when row grouping
      const headerName = column.getColDef().headerName ?? field;
      let headerNameUppercase = '';
      if (headerName && headerName.length > 0) {
        headerNameUppercase = headerName[0].toUpperCase() + headerName.slice(1);
      }
      const headerCell = {
        text: headerNameUppercase + (sort ? ` (${sort})` : ''),

        // styles
        bold: true,
        fontSize: 8,
      };
      return headerCell;
    });
  };

  /**
   * This function iterates over all of the rows and columns to create
   * a matrix of cells when pivoting is enabled
   */

  const getRowsToExportPivot = (gridApi: AgGridReact) => {
    const columns = gridApi.api.getAllDisplayedColumns();

    const getCellToExport = (column: Column, node: IRowNode) => ({
      text: gridApi.api.getValue(column, node) ?? '',
      // styles
      ...column.getColDef().cellStyle,
    });

    const rowsToExport: any = [];
    gridApi.api.forEachNodeAfterFilterAndSort((node: IRowNode) => {
      if (node.group) {
        const rowToExport = columns.map((column) => getCellToExport(column, node));
        rowsToExport.push(rowToExport);
      }
    });

    return rowsToExport;
  };

  /**
   * This function iterates over all of the rows and columns to create
   * a matrix of cells
   */
  const getRowsToExport = (gridApi: AgGridReact) => {
    //Enables export when pivoting
    if (gridApi.api.isPivotMode()) {
      return getRowsToExportPivot(gridApi);
    }
    const columns = gridApi.api.getAllDisplayedColumns();

    const getCellToExport = (column: Column, node: IRowNode) => {
      return {
        text: gridApi.api.getValue(column, node) ?? '',
        // styles
        ...column.getColDef().cellStyle,
        fontSize: 8,
        noWrap: true,
      };
    };

    const rowsToExport: any = [];
    gridApi.api.forEachNodeAfterFilterAndSort((node) => {
      const rowToExport = columns.map((column: Column) => getCellToExport(column, node));
      rowsToExport.push(rowToExport);
    });

    return rowsToExport;
  };

  const getPinnedBottomRowsToExport = (gridApi: AgGridReact) => {
    const columns = gridApi.api.getAllDisplayedColumns();

    const getCellToExport = (column: Column, node?: IRowNode) => {
      return {
        text: node ? gridApi.api.getValue(column, node) ?? '' : '',
        // styles
        ...column.getColDef().cellStyle,
        bold: true,
        fontSize: 8,
        noWrap: true,
      };
    };

    const node = gridApi.api.getPinnedBottomRow(0);
    const rowsToExport: any = [];
    const rowToExport = columns.map((column: Column) => getCellToExport(column, node));
    rowsToExport.push(rowToExport);
    return rowsToExport;
  };

  // Row colors
  const HEADER_ROW_COLOR = '#f8f8f8';
  const EVEN_ROW_COLOR = '#fcfcfc';
  const ODD_ROW_COLOR = '#fff';

  const PDF_INNER_BORDER_COLOR = '#dde2eb';
  const PDF_OUTER_BORDER_COLOR = '#babfc7';

  const createLayout = (numberOfHeaderRows: number) => ({
    fillColor: (rowIndex: number) => {
      if (rowIndex < numberOfHeaderRows) {
        return HEADER_ROW_COLOR;
      }
      return rowIndex % 2 === 0 ? EVEN_ROW_COLOR : ODD_ROW_COLOR;
    },
    //vLineHeight not used here.
    vLineWidth: (rowIndex: number, node: ContentTable) =>
      rowIndex === 0 || rowIndex === node.table.widths?.length ? 1 : 0,
    hLineColor: (rowIndex: number, node: ContentTable) =>
      rowIndex === 0 || rowIndex === node.table.body.length ? PDF_OUTER_BORDER_COLOR : PDF_INNER_BORDER_COLOR,
    vLineColor: (rowIndex: number, node: ContentTable) =>
      rowIndex === 0 || rowIndex === node.table.widths?.length ? PDF_OUTER_BORDER_COLOR : PDF_INNER_BORDER_COLOR,
  });

  /**
   * Returns a pdfMake shaped config for export, for more information
   * regarding pdfMake configuration, please see the pdfMake documentation.
   */
  const getDocument = (gridApi: AgGridReact, hasGroupHeader?: boolean) => {
    const columns = gridApi.api.getAllDisplayedColumns();

    const headerRow = getHeaderToExport(gridApi);
    const rows = getRowsToExport(gridApi);
    const bottoms = getPinnedBottomRowsToExport(gridApi);
    return {
      defaultStyle: {
        font: 'SpoqahanSansNeo',
      },
      language: 'ko-KR',
      pageSize: 'A3' as PredefinedPageSize,
      pageOrientation: 'landscape' as PageOrientation, // can also be 'portrait'
      content: [
        {
          table: {
            // the number of header rows
            headerRows: 1,

            // the width of each column, can be an array of widths
            // widths: [`auto`],
            // widths: ['*', '*', '*', '*'],

            // all the rows to display, including the header rows
            body: [headerRow, ...rows, ...bottoms],

            // Header row is 40px, other rows are 15px
            heights: (rowIndex: number) => (hasGroupHeader && rowIndex === 0 ? 40 : 'auto'),
          },
          layout: createLayout(1),
        },
      ],
      pageMargins: 10,
    };
  };

  const exportToPdf = (gridApi: AgGridReact, fileName: string) => {
    const doc = getDocument(gridApi);
    pdfMake.createPdf(doc).download(fileName);
  };

  return { exportToPdf };
};

export default useGridExport;
